import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import { OUTBOX_STATUS_LABELS, type OutboxStatus } from "@shared/modules/x-messagerie";
import { api } from "../../../core/lib/api";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";
import { Textarea } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { useToastStore } from "../../../core/stores/toast";
import { formatDateTime } from "../../../core/lib/format";
import { MessageBubble } from "../components/bubble";
import { initials, prettyPhone, quand } from "../lib/texte";

interface Conversation {
  phone: string;
  lastAt: number;
  unread: number;
  stopped: boolean;
  /** Dernier texto reçu, pour l'aperçu de la liste. */
  lastBody: string | null;
  client: { id: string; name: string } | null;
}
interface Detail {
  phone: string;
  stopped: boolean;
  messages: { id: string; body: string; at: number; direction: string; status: string }[];
}

/** Statut d'un texto envoyé, en un mot ; rien pour « envoyé », le cas normal. */
function statut(status: string): string {
  if (status === "envoye" || status === "recu") return "";
  return OUTBOX_STATUS_LABELS[status as OutboxStatus] ?? status;
}

/**
 * Boîte de réception des textos, comme une messagerie : la liste à gauche (initiales, nom,
 * dernier message, non lus), le fil à droite en bulles avec le composeur. Refonte du
 * 26 octobre 2026 à la demande du propriétaire.
 */
export function ConversationsPage() {
  const push = useToastStore((s) => s.push);
  const [rows, setRows] = useState<Conversation[] | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fil = useRef<HTMLOListElement>(null);

  const refresh = () =>
    api<Conversation[]>("/api/messagerie/conversations")
      .then(setRows)
      .catch(() => push("danger", "Chargement impossible"));
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Le fil s'ouvre sur le dernier message.
  useEffect(() => {
    if (fil.current) fil.current.scrollTop = fil.current.scrollHeight;
  }, [detail]);

  const open = async (phone: string) => {
    try {
      const d = await api<Detail>(`/api/messagerie/conversations/${encodeURIComponent(phone)}`);
      setDetail(d);
      setText("");
      const until = Math.max(
        0,
        ...d.messages.filter((m) => m.direction === "entrant").map((m) => m.at),
      );
      await api(`/api/messagerie/conversations/${encodeURIComponent(phone)}/read`, {
        method: "POST",
        body: JSON.stringify({ until }),
      });
      void refresh();
    } catch {
      push("danger", "Conversation indisponible");
    }
  };

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!detail || !text.trim() || busy) return;
    setBusy(true);
    try {
      const result = await api<{ status: string }>("/api/messagerie/outbox", {
        method: "POST",
        body: JSON.stringify({ channel: "sms", to: detail.phone, body: text }),
      });
      if (result.status === "envoye") push("success", "Texto envoyé");
      else if (result.status === "simule")
        push("warning", "Texto simulé", "Aucune clé d'envoi sur ce serveur : rien n'est parti.");
      else push("danger", "Envoi en échec", `Statut : ${result.status}`);
      await open(detail.phone);
    } catch {
      push("danger", "Envoi impossible");
    } finally {
      setBusy(false);
    }
  };

  // Ctrl/Cmd + Entrée envoie ; Entrée seule garde le retour à la ligne.
  const raccourci = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void send();
    }
  };

  const courant = rows?.find((r) => r.phone === detail?.phone) ?? null;
  const nom = courant?.client?.name ?? null;

  return (
    <div className="page conversations">
      <PageHeader
        title="Conversations SMS"
        subtitle="Réponses reçues et échanges avec vos clients."
        actions={
          <Button variant="ghost" onClick={() => void refresh()}>
            Actualiser
          </Button>
        }
      />
      <div className="conversations__layout">
        <aside className="conversations__liste" aria-label="Conversations">
          {rows === null ? (
            <p className="messages__muted">Chargement…</p>
          ) : rows.length === 0 ? (
            <p className="messages__muted">Aucun texto reçu.</p>
          ) : (
            rows.map((r) => {
              const classes = ["convo"];
              if (detail?.phone === r.phone) classes.push("convo--active");
              if (r.unread) classes.push("convo--non-lu");
              return (
                <button
                  key={r.phone}
                  type="button"
                  className={classes.join(" ")}
                  onClick={() => void open(r.phone)}
                  aria-current={detail?.phone === r.phone ? "true" : undefined}
                >
                  <span className="convo__avatar" aria-hidden="true">
                    {initials(r.client?.name)}
                  </span>
                  <span className="convo__corps">
                    <span className="convo__ligne">
                      <span className="convo__nom">{r.client?.name ?? prettyPhone(r.phone)}</span>
                      <span className="convo__quand">{quand(r.lastAt)}</span>
                    </span>
                    <span className="convo__ligne">
                      <span className="convo__apercu">
                        {r.stopped ? "A refusé les textos (STOP)" : (r.lastBody ?? "")}
                      </span>
                      {r.unread ? (
                        <span className="convo__badge" aria-label={`${r.unread} non lu(s)`}>
                          {r.unread}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </aside>

        <section className="conversations__fil" aria-label="Fil de la conversation">
          {detail ? (
            <>
              <header className="fil__entete">
                <span className="convo__avatar" aria-hidden="true">
                  {initials(nom)}
                </span>
                <div className="fil__identite">
                  <strong>{nom ?? prettyPhone(detail.phone)}</strong>
                  {nom ? (
                    <span className="messages__muted">{prettyPhone(detail.phone)}</span>
                  ) : (
                    <span className="messages__muted">Numéro inconnu de vos fiches</span>
                  )}
                </div>
                {courant?.client ? (
                  <Link className="btn btn--ghost fil__fiche" to={`/clients/${courant.client.id}`}>
                    Fiche client
                  </Link>
                ) : null}
              </header>
              {detail.stopped ? (
                <p className="fil__stop" role="status">
                  Ce numéro a refusé les communications (STOP). L’envoi de textos est bloqué.
                </p>
              ) : null}
              <ol className="fil__messages" ref={fil}>
                {detail.messages.map((m) => {
                  const entrant = m.direction === "entrant";
                  const etat = statut(m.status);
                  return (
                    <li
                      key={m.id}
                      className={`fil__item ${entrant ? "fil__item--entrant" : "fil__item--sortant"}`}
                    >
                      <MessageBubble channel="sms" body={m.body} sortant={!entrant} />
                      <span
                        className="fil__meta"
                        title={formatDateTime(m.at, { dateStyle: "medium", timeStyle: "short" })}
                      >
                        {quand(m.at)}
                        {etat ? ` · ${etat}` : ""}
                      </span>
                    </li>
                  );
                })}
              </ol>
              <form className="fil__composeur" onSubmit={send}>
                <Textarea
                  aria-label="Votre réponse"
                  rows={2}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={raccourci}
                  maxLength={1600}
                  disabled={detail.stopped}
                  placeholder={
                    detail.stopped
                      ? "Envoi bloqué"
                      : "Votre réponse… ({{clientPrenom}} est remplacé)"
                  }
                />
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy || detail.stopped || !text.trim()}
                  title="Ctrl + Entrée pour envoyer"
                >
                  {busy ? "Envoi…" : "Envoyer"}
                </Button>
              </form>
            </>
          ) : (
            <div className="fil__vide">
              <Icon name="sms" size={32} />
              <p>Choisissez une conversation pour lire les échanges et répondre.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
